import React from 'react';
import { Container, Box, Typography, Grid, Paper, Avatar, Card, CardMedia } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const About = () => {
  const theme = useTheme();

  const highlightedMatters = [
    {
      title: 'Industrial Pollution Compliance Before NGT',
      image: '/images/work-industrial-compliance.jpg',
      description:
        'Represented State authorities in a series of matters concerning industrial effluent discharge, securing time-bound compliance plans and monitoring mechanisms.',
    },
    {
      title: 'River Conservation and Sewage Management',
      image: '/images/work-river-conservation.jpg',
      description:
        'Appeared for Delhi Jal Board and other agencies in litigation focused on river pollution, sewage treatment plants and long-term rejuvenation projects.',
    },
    {
      title: 'Environmental Clearances & Impact Assessment',
      image: '/images/work-eia-clearances.jpg',
      description:
        'Advised and represented in disputes relating to environmental clearances, environmental impact assessment and compliance with SEIAA directions.',
    },
  ];

  const education = [
    {
      degree: 'BA. LL.B (Hons.)',
      institution: 'University of Lucknow, Faculty of Law',
      year: '2005 – 2010',
    },
    {
      degree: 'BA. LL.B (Hons.)',
      institution: 'Amity University, Greater Noida Campus',
      year: '2018 – 2023',
    },
    {
      degree: 'Schooling',
      institution: 'Delhi Public School (DPS), Gurgaon',
      year: '2004 – 2018',
    },
  ];

  const experience = [
    {
      role: 'Standing Counsel',
      firm: 'State of Uttar Pradesh before the National Green Tribunal (NGT)',
      period: 'Present',
    },
    {
      role: 'Senior Panel Counsel',
      firm: 'Union of India before the NGT',
      period: 'Present',
    },
    {
      role: 'Counsel',
      firm: 'Delhi Jal Board (DJB) & SEIAA, Uttar Pradesh',
      period: 'Present',
    },
    {
      role: 'Senior Associate (Legal & IP Prosecution)',
      firm: 'SagaciousElevate',
      period: 'Present',
    },
    {
      role: 'Associate',
      firm: 'Lex Paramount Legal',
      period: 'Previous',
    },
    {
      role: 'Advocate',
      firm: 'Independent practice in environmental, civil, criminal and commercial matters',
      period: 'Prior & Present',
    },
    {
      role: 'Intern',
      firm: 'Saket Courts, New Delhi (Criminal Law Practice)',
      period: 'Early Career',
    },
  ];

  const achievements = [
    'Represents key government bodies in environmental matters before the National Green Tribunal (NGT)',
    'Extensive experience in pollution control, compliance and environmental impact assessment litigation',
    'Senior Panel Counsel for the Union of India and Standing Counsel for the State of Uttar Pradesh',
    'Senior Associate (Legal & IP Prosecution) with specialised experience in intellectual property and corporate work',
    'Certifications in Media Laws, RTI and Intellectual Property from platforms such as Enhelion',
  ];

  return (
    <Container sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Avatar
          src="/images/priyanka-portrait.jpg"
          sx={{
            width: 200,
            height: 200,
            mx: 'auto',
            mb: 3,
            bgcolor: theme.palette.primary.main,
            fontSize: '4rem',
          }}
        >
          PS
        </Avatar>
        <Typography variant="h3" gutterBottom>
          About Advocate Priyanka Swami
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          An environmental lawyer and versatile litigator, representing key government bodies before
          the National Green Tribunal while also advising and appearing in civil, criminal and
          commercial matters.
        </Typography>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          About Advocate Priyanka Swami
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          Founded in 2010, Advocate Priyanka Swami has been a trusted name in providing expert
          legal solutions across Delhi, NCR, and before the National Green Tribunal. Led by the
          esteemed Advocate Priyanka Swami, our practice offers a one-stop legal service hub —
          combining advocates, solicitors, and legal consultants under one roof. We understand the
          challenges of today&apos;s dynamic legal environment and are committed to delivering
          tailored, efficient, and reliable legal support to individuals, businesses, and government
          institutions alike.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          Advocate Priyanka Swami is primarily an environmental lawyer, serving as Standing Counsel
          for the State of Uttar Pradesh and Senior Panel Counsel for the Union of India before the
          National Green Tribunal (NGT). She regularly represents government bodies such as the
          Delhi Jal Board (DJB) and the State Environment Impact Assessment Authority (SEIAA),
          Uttar Pradesh, in matters relating to pollution control, compliance and environmental
          regulation.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          Alongside her core environmental practice, she has experience across general civil,
          criminal and commercial matters, having begun her career in the criminal courts at Saket,
          New Delhi. This breadth of exposure allows her to approach disputes with a holistic view
          of both regulatory and trial‑court realities. At Advocate Priyanka Swami, your legal needs
          are handled with professionalism, dedication, and care.
        </Typography>
      </Box>

      <Box sx={{ mb: 6, bgcolor: 'background.default', p: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
          Our Best Service Benefits
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Comprehensive Single-Window Solutions
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Experienced Legal Team
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Client-Focused Approach
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Efficient & Timely Support
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Trusted Legal Guidance
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Wide Range of Services
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Strong Presence in Delhi/NCR
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>•</strong> Confidentiality & Integrity
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <SchoolIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Education
            </Typography>
            {education.map((edu, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {edu.degree}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {edu.institution}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {edu.year}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <WorkIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Experience
            </Typography>
            {experience.map((exp, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {exp.role}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {exp.firm}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {exp.period}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <EmojiEventsIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Achievements
            </Typography>
            {achievements.map((achievement, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                • {achievement}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Highlighted Matters
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 900 }}>
          A brief selection of matters that reflect the breadth of Advocate Priyanka Swami&apos;s
          work in environmental and allied fields. Images are indicative and used for visual
          representation.
        </Typography>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {highlightedMatters.map((matter, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={matter.image}
                  alt={matter.title}
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 2, flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {matter.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {matter.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Philosophy
        </Typography>
        <Typography variant="body1" paragraph>
          I believe that everyone deserves access to quality legal representation. My approach
          combines deep subject‑matter expertise with practical, solution‑oriented advice,
          ensuring that clients and public institutions receive clear, accountable and effective
          representation. I am committed to:
        </Typography>
        <Box component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1">
              Providing transparent and honest legal advice
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Maintaining the highest ethical standards
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Fighting tirelessly for client rights and interests
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Building long-term relationships based on trust
            </Typography>
          </li>
        </Box>
      </Box>
    </Container>
  );
};

export default About;

